import {
  CHECK_CVV_ACTION,
  FRAME_NAMES,
  TOKENIZE_ACTION,
} from "../../constants";
import { fetchCheckCvv, fetchToken } from "../../api/api";
import { getExpireDateParts } from "../common";
import {
  CardData,
  RequestToCheckCVVProps,
  RequestToTokenizeProps,
} from "./types";
import { isValid } from "./cardValidator";
import {
  cardValidationAction,
  checkCvvFailedAction,
  checkCvvSuccessAction,
  tokenizeFailedAction,
  tokenizeSuccessAction,
} from "../../actions";
import { isTokenizeErrorResponse } from "../../api/tokenizeTypes";
import { notifyParent } from "../iframeNotifications";

let cardData: Partial<CardData> = {};

let isCardValidPrev = false;

export const collectData = (event: MessageEvent) => {
  const { origin, data } = event;

  if (origin !== location.origin) {
    return;
  }

  const hasAppropriateData = Object.values(FRAME_NAMES).some(
    (fieldName) => fieldName in data
  );

  if (!hasAppropriateData) {
    return;
  }

  cardData = Object.assign(cardData, data);

  const isCardValid = isValid(cardData);

  if (isCardValidPrev !== isCardValid) {
    isCardValidPrev = isCardValid;
    notifyParent(cardValidationAction(isCardValid));
  }
};

export const requestToPay = (event: MessageEvent<RequestToTokenizeProps>) => {
  const {
    source,
    data: { type, payload, options },
  } = event;

  if (source !== parent) {
    return;
  }

  if (type === TOKENIZE_ACTION) {
    if (!isValid(cardData)) {
      notifyParent(tokenizeFailedAction({ message: "invalid card data" }));
    }

    const { stored, cardholder, accessToken, accountId } = payload;
    const [expMonth, expYear] = getExpireDateParts(cardData.date);

    // TODO: also check dev mode
    if (options.hasToMock) {
      notifyParent(
        tokenizeSuccessAction({
          token: "token",
          card: {
            bin: "bin123",
            expYear,
            expMonth,
            last4: cardData.number.substring(cardData.number.length - 4),
            stored: true,
            cardholder,
          },
        })
      );

      return;
    }

    fetchToken({
      cvv: cardData.cvv,
      cardNumber: cardData.number,
      accountId,
      expMonth,
      expYear,
      cardholder,
      stored,
      accessToken,
    })
      .then((response) => {
        if (isTokenizeErrorResponse(response)) {
          notifyParent(tokenizeFailedAction(response.error));
        } else {
          notifyParent(tokenizeSuccessAction(response));
        }
      })
      .catch(() => {
        notifyParent(tokenizeFailedAction({ message: "cannot process data" }));
      });
  }
};

const checkCVV = (event: MessageEvent<RequestToCheckCVVProps>) => {
  const {
    source,
    data: { type, payload, options },
  } = event;

  if (source !== parent) {
    return;
  }

  if (type !== CHECK_CVV_ACTION) {
    return;
  }

  if (options.hasToMock) {
    const date = new Date();
    notifyParent(
      checkCvvSuccessAction({
        token: "token",
        card: {
          bin: "bin123",
          expYear: `${date.getFullYear()}`,
          expMonth: `${date.getMonth()}`,
          last4: "4242",
          stored: true,
          cardholder: "Name",
        },
      })
    );
    return;
  }

  fetchCheckCvv({ ...payload, cvv: cardData.cvv })
    .then((response) => {
      if (isTokenizeErrorResponse(response)) {
        notifyParent(checkCvvFailedAction(response.error));
      } else {
        notifyParent(checkCvvSuccessAction(response));
      }
    })
    .catch(() => {
      notifyParent(checkCvvFailedAction({ message: "cannot process data" }));
    });
};

window.addEventListener("message", collectData, false);
window.addEventListener("message", requestToPay, false);
window.addEventListener("message", checkCVV, false);
